import { atom, type Atom } from 'jotai';

import { SelectedSiteCloudIdAtom } from '../../../../state/LabsAtoms';
import { queryBuilderGraphQL, queryBuilderRequest } from '../../api';

type SchemaKind = 'canonical' | 'logical' | 'dynamic';
export type SchemaAlias = {
	name: string;
	isLatest: boolean;
	isAliasExternallyAvailable: boolean;
	inverse?: boolean | null;
};
export type SchemaNode = {
	name: string;
	kind: SchemaKind;
	aliases: SchemaAlias[];
	graphQLTypes: string[];
	properties: string[];
	deleted: boolean;
};
export type SchemaRelationship = {
	name: string;
	kind: SchemaKind;
	aliases: SchemaAlias[];
	fromLabel: string[];
	targetLabel: string[];
	deleted: boolean;
	hidden: boolean;
	activity: boolean;
	properties?: string[];
};
type GraphSchema = {
	nodes: SchemaNode[];
	relationships: SchemaRelationship[];
	unavailableSources: string[];
};

const globalSchemaAtom = atom(() => queryBuilderRequest<GraphSchema>('twg/graph-schema/global'));

const dynamicSchemaAtom = atom(async (get): Promise<GraphSchema> => {
	const cloudId = get(SelectedSiteCloudIdAtom);
	const empty: GraphSchema = { nodes: [], relationships: [], unavailableSources: [] };
	if (!cloudId) {
		return empty;
	}
	try {
		const data = await queryBuilderGraphQL<{
			tenantContexts: ({ activationIdByProduct: { active: string | null } | null } | null)[];
		}>(
			'QueryBuilder_AssetsWorkspace',
			`query QueryBuilder_AssetsWorkspace($cloudIds: [ID!]!) {
				tenantContexts(cloudIds: $cloudIds) {
					activationIdByProduct(product: "jsm-cmdb") { active }
				}
			}`,
			{ cloudIds: [cloudId] },
		);
		const workspaceId = data.tenantContexts[0]?.activationIdByProduct?.active;
		if (!workspaceId) {
			return empty;
		}
		const workspaceAri = `ari:cloud:cmdb::workspace/${workspaceId}`;
		return await queryBuilderRequest<GraphSchema>(
			`twg/graph-schema/dynamic?workspaceAri=${encodeURIComponent(workspaceAri)}`,
		);
	} catch {
		return { ...empty, unavailableSources: ['Assets schema'] };
	}
});

export const queryBuilderGraphSchemaAtom: Atom<Promise<GraphSchema>> = atom(async (get) => {
	const [global, dynamic] = await Promise.all([get(globalSchemaAtom), get(dynamicSchemaAtom)]);
	return {
		nodes: [...global.nodes, ...dynamic.nodes],
		relationships: [...global.relationships, ...dynamic.relationships],
		unavailableSources: [...global.unavailableSources, ...dynamic.unavailableSources],
	};
});

export const publicAlias = (aliases: SchemaAlias[]): SchemaAlias | undefined =>
	aliases.find((alias) => alias.isLatest && alias.isAliasExternallyAvailable);

export const queryBuilderNodesAtom: Atom<Promise<Record<string, SchemaNode>>> = atom(
	async (get) => {
		const schema = await get(queryBuilderGraphSchemaAtom);
		return Object.fromEntries(
			schema.nodes.filter((node) => !node.deleted).map((node) => [node.name, node]),
		);
	},
);
